import { useCallback, useRef, useState } from 'react';

import { designsForPair, type LoadedDesigns } from './copy-channel-settings';
import type { DesignLoad } from './load-pype-next-design';

/**
 * Loads the Pype Next designs of a source/target pair and hands them out only
 * for that pair. Two things keep a late answer off the current screen:
 *
 * - `token` is bumped by every `start` and every `cancel` (Back, close, reset),
 *   and a load that finds the token moved when it finishes drops its result;
 * - the result is tagged with the ids it was loaded for, and `designsForPair`
 *   returns it only while the `source` and `target` passed in still carry them.
 *
 * Limitation: an abandoned load is not aborted, so its two requests run to
 * their own deadline; only their result is ignored.
 */
export const usePairDesigns = <C extends { id: string }>(
  source: C | null,
  target: C | null,
  load: (channel: C) => Promise<DesignLoad>
) => {
  const [loaded, setLoaded] = useState<LoadedDesigns | null>(null);
  const token = useRef(0);

  const cancel = useCallback(() => {
    token.current += 1;
    setLoaded(null);
  }, []);

  const start = useCallback(async () => {
    const mine = ++token.current;
    setLoaded(null);
    if (!source || !target) return;
    const [s, t] = await Promise.all([load(source), load(target)]);
    if (mine !== token.current) return;
    setLoaded({
      sourceId: source.id,
      targetId: target.id,
      source: s,
      target: t,
    });
  }, [source, target, load]);

  return { designs: designsForPair(loaded, source, target), start, cancel };
};
